/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect} from "react";
import {useTranslation} from "react-i18next";
import {RouteComponentProps, withRouter} from "react-router";
import useBookingPackagesView from "pages/Front/Booking/components/BookingPackageNew/hooks/useBookingPackagesView";
import BookingPackagesViewSkeleton
    from "pages/Front/Booking/components/BookingPackageNew/components/BookingPackagesViewSkeleton";
import BookingPackagesViewFooter
    from "pages/Front/Booking/components/BookingPackageNew/components/BookingPackagesViewFooter";
import BookingPackagesCreditsFamilyDriverRow
    from "pages/Front/Booking/components/BookingPackageNew/components/BookingPackagesCreditsFamilyDriverRow";
import CreditsInfo from "pages/Front/Booking/Credits/components/CreditsInfo";

export interface BookingPackagesFamilyViewParams {}

interface BookingPackagesFamilyViewProps extends RouteComponentProps<BookingPackagesFamilyViewParams> {}

const BookingPackagesFamilyView = (props: BookingPackagesFamilyViewProps) => {
    const {t} = useTranslation();
    const {
        availablePackageItems,
        availableCreditItems,
        selectedPackageItems,
        selectedPackageCreditItems,
        filter,
        allFilled,
        handleRouteNavigation,
        onNextStepHandler,
        handleChange,
        setDefaultPackageCreditItems,
    } = useBookingPackagesView(props.history, true);

    useEffect(() => {
        if (!availablePackageItems?.packageItems || availableCreditItems.length === 0 || selectedPackageItems.length === 0 || selectedPackageCreditItems.length > 0) {
            return;
        }

        setDefaultPackageCreditItems(true, selectedPackageItems);
    }, [availablePackageItems, availableCreditItems, selectedPackageItems, selectedPackageCreditItems.length, filter.adults, filter.children]);

    if (!availablePackageItems?.packageItems || !selectedPackageItems) {
        return <BookingPackagesViewSkeleton className={"BookingPackagesFamilyView"} />;
    }

    return (
        <div className={"BookingPackagesFamilyView w-full"}>
            <div className={"BookingPackagesFamilyViewWrapper border rounded-xl bg-white shadow w-full"}>
                <div
                    className={
                        "BookingPackagesFamilyViewHeader grid grid-cols-1 xs:grid-cols-2 lg:flex lg:flex-row items-center rounded-t-xl w-full bg-[#F5F5F566]/40 border-b border-[#D9D9D9] px-4 py-4 lg:px-3 xl:px-4 lg:py-3 xl:py-4 gap-y-3 sm:gap-y-2 gap-x-4 xl:gap-8"
                    }
                >
                    <div className="flex items-center gap-2 xl:gap-3 order-1 lg:order-1">
                        <img src="/img/credits/credits-badge-green.svg" alt="family tickets badge icon" className="size-[32px] md:size-[40px] flex-none" />
                        <h2 className={"font-bold text-xl xl:text-2xl"}>{t("Family tickets")}</h2>
                    </div>

                    <div className={"w-px h-8 bg-[#D9D9D9] hidden lg:block lg:order-2"} />

                    <div className="flex flex-col md:flex-row items-start xs:items-end md:items-center gap-0 md:gap-3 order-3 xs:order-4 lg:order-3 sm:justify-self-end lg:justify-self-auto">
                        <span className={"font-semibold text-lg md:text-xl shrink-0"}>
                            {filter.children + filter.adults} {t("persons")}
                        </span>
                        <span className={"text-sm md:text-sm xl:text-base leading-tight"}>
                            ({filter.adults} {t("Adults")}, {filter.children} {t("Children")})
                        </span>
                    </div>

                    <div className={"w-px h-8 bg-[#D9D9D9] hidden lg:block lg:order-4"} />

                    <div className="flex flex-col md:flex-row items-start md:items-center gap-0 md:gap-3 order-2 xs:order-3 lg:order-5">
                        <div className={"text-lg md:text-xl font-semibold text-[#68B40B] shrink-0"}>{t("50% discount")}</div>
                        <div className={"text-sm md:text-sm xl:text-base leading-tight"}>{t("Children 8-15 years old")}</div>
                    </div>

                    {/*<div className="sm:ml-auto order-4 xs:order-2 lg:order-6 xs:justify-self-end lg:justify-self-auto">*/}
                    {/*    <button*/}
                    {/*        type={"button"}*/}
                    {/*        onClick={handleChange}*/}
                    {/*        className={*/}
                    {/*            "w-full xs:w-auto flex items-center justify-center gap-2 rounded border border-mid-grey bg-[#878181] px-2.5 py-2 text-xs font-semibold text-white hover:border-black/70 transition-colors"*/}
                    {/*        }*/}
                    {/*    >*/}
                    {/*        <span>{t("change")}</span>*/}
                    {/*    </button>*/}
                    {/*</div>*/}
                </div>

                <div className={"BookingPackagesFamilyViewContent flex flex-col px-2 lg:px-4 xl:px-6 py-4 lg:py-6 w-full"}>
                    {selectedPackageItems.length > 0 && <BookingPackagesCreditsFamilyDriverRow category={filter.category} />}
                </div>

                <CreditsInfo />

                <BookingPackagesViewFooter
                    className={"BookingPackagesFamilyViewFooter"}
                    discountImageAlt={"family tickets discount"}
                    onBack={handleRouteNavigation}
                    onContinue={onNextStepHandler}
                    disabled={!allFilled}
                />
            </div>
        </div>
    );
};

export default withRouter(BookingPackagesFamilyView);
